import React, { useMemo } from "react";
import Modal from "../../../components/modal/modal";
import { toUSD } from "../../../util/currency-formatter";
import { formatDecimalTime } from "../../../util/common";
import { formatTimeTypeLabel } from "../util/timesheet-breakdown";
import { toDecimal, roundValue } from "../../../util/decimal-utils";

/**
 * Displays work hour breakdown, tier rates, costs, and actions for a timesheet allocation.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.open - Modal open state.
 * @param {Object} props.allocation - Selected timesheet allocation record.
 * @param {Function} props.onClose - Modal close handler.
 * @param {Function} props.onSubmit - Approval submission handler.
 * @param {Function} [props.onModify] - Handler to navigate to timesheet modification.
 * @returns {React.ReactElement} Manual review hours modal component.
 */
const ManualReviewHoursModal = ({ open, allocation, onClose, onSubmit, onModify }) => {
    const originalDuration = useMemo(() => {
        if (!allocation?.startTime || !allocation?.endTime) return 0;
        const start = new Date(allocation.startTime);
        const end = new Date(allocation.endTime);
        const durationInHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return Number.isFinite(durationInHours) && durationInHours > 0 ? durationInHours : 0;
    }, [allocation]);

    const breakdown = allocation?.work_hour_breakdown;
    const timePeriods = breakdown?.time_periods;
    const hasBreakdown = Array.isArray(timePeriods) && timePeriods.length > 0;

    const totalCost = useMemo(() => {
        if (!hasBreakdown) return null;
        let sum = toDecimal(0);
        timePeriods.forEach((p) => {
            sum = sum.plus(toDecimal(p.cost));
        });
        return roundValue(sum, 2);
    }, [hasBreakdown, timePeriods]);

    const classifiedHours = useMemo(() => {
        if (!hasBreakdown) return 0;
        let sum = toDecimal(0);
        timePeriods.forEach((p) => {
            sum = sum.plus(toDecimal(p.hours));
        });
        return roundValue(sum, 2);
    }, [hasBreakdown, timePeriods]);

    const unclassifiedHours = hasBreakdown
        ? roundValue(toDecimal(originalDuration).minus(toDecimal(classifiedHours)), 2)
        : 0;
    const hasUnclassified = unclassifiedHours > 0.05;

    const handleSave = () => {
        onSubmit({ originalDuration });
    };

    const isPending = allocation?.status === 'PENDING';
    const isManualReview = isPending && Boolean(allocation?.requiresManualReview);
    const modalHeading = isManualReview ? "Manual Review Required" : "Work Hour Breakdown";

    return (
        <Modal
            fullWidth={true}
            maxWidth="sm"
            save={isPending ? <span data-cy="manual-review-save">Approve</span> : null}
            cancel={isPending ? "Cancel" : "Close"}
            footerAdditionalbtnTxt={isPending ? "Modify" : null}
            footerAdditionalbtn={isPending ? onModify : undefined}
            onClick={onClose}
            onEdit={isPending ? handleSave : undefined}
            open={open}
            onClose={onClose}
            disableBackdropClick={true}
            modalHeading={modalHeading}
        >
            <div className="manual-review-hours-modal" data-cy="manual-review-modal">
                <p className="manual-review-hours-modal__hint" data-cy="manual-review-hint">
                    Total duration: <b>{formatDecimalTime(originalDuration)}</b>.{' '}
                    {hasBreakdown
                        ? <>Breakdown calculated from <b>{breakdown.setting_name}</b> work hour configuration.</>
                        : 'The work hour engine has not yet computed the breakdown for this entry.'
                    }
                </p>

                {hasBreakdown ? (
                    <div className="manual-review-breakdown" data-cy="manual-review-breakdown">
                        <p className="mb-2"><b>Cost breakdown (from work hour configuration):</b></p>
                        <table className="table table-sm mb-2" style={{ fontSize: '0.875rem' }}>
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th className="text-center">Hours</th>
                                    <th className="text-center">Rate</th>
                                    <th className="text-right">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timePeriods.map((p, i) => (
                                    <tr key={i}>
                                        <td>
                                            {formatTimeTypeLabel(p.time_type)}
                                            {p.time_type !== 'UNPAID_ALLOWANCE' && <span className="ms-1" style={{ fontSize: '0.8rem' }}>({p.multiplier}×)</span>}
                                        </td>
                                        <td className="text-center">{formatDecimalTime(p.hours)}</td>
                                        <td className="text-center">{toUSD(p.hourly_rate)}/h</td>
                                        <td className="text-right">{toUSD(parseFloat(p.cost) || 0)}</td>
                                    </tr>
                                ))}
                                {hasUnclassified && (
                                    <tr style={{ color: '#f59e0b' }}>
                                        <td>⚠ Unclassified</td>
                                        <td className="text-center">{formatDecimalTime(unclassifiedHours)}</td>
                                        <td className="text-center">—</td>
                                        <td className="text-right">—</td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={3}><b>Total</b></td>
                                    <td className="text-right"><b>{toUSD(totalCost)}</b></td>
                                </tr>
                            </tfoot>
                        </table>
                        {hasUnclassified && (
                            <p className="text-warning mb-0" style={{ fontSize: '0.8rem' }}>
                                ⚠ {formatDecimalTime(unclassifiedHours)} fall outside the configured work hour periods and are not included in the cost calculation. Review the Work Hour configuration to ensure full-day coverage.
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="manual-review-breakdown manual-review-breakdown--missing" data-cy="manual-review-breakdown-missing">
                        <p className="text-warning mb-0" style={{ fontSize: '0.875rem' }}>
                            {isPending
                                ? 'No configuration breakdown available. The entry may have been submitted before a work hour setting was assigned to this user, or the calculation is still pending. Approve only if the hours are correct.'
                                : 'No configuration breakdown available for this entry.'
                            }
                        </p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ManualReviewHoursModal;
